import { Box, InfoDot, Text } from '@factorialco/deprecated-design-system'
import { AtsCandidatesDataTableQuery } from 'generated/resources/datatable.graphql'
import { uniq } from 'lodash'

import { diffBetweenDateTimes } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getProperties } from 'modules/ats/lib/utils/hiringStages'
import { isJobActive } from 'modules/ats/lib/utils/jobPosting'

type DataTableRow =
  AtsCandidatesDataTableQuery['ats']['candidatesConnection']['edges'][number]['node']
type RowApplication = DataTableRow['applications'][number]

const prioritizeApplications = (a: RowApplication, b: RowApplication) => {
  // Hired applications first
  const aHired = +(a.applicationPhase.hiringStage?.name == 'hired')
  const bHired = +(b.applicationPhase.hiringStage?.name == 'hired')
  if (aHired !== bHired) return bHired - aHired

  // Qualified applications first
  const aQualified = +(a.qualified === true)
  const bQualified = +(b.qualified === true)
  if (aQualified !== bQualified) return bQualified - aQualified

  // Applications for active jobs first
  const aActive = +isJobActive(a.jobPosting.status)
  const bActive = +isJobActive(b.jobPosting.status)
  if (aActive !== bActive) return bActive - aActive

  // Applications on latter stages first
  const aStagePosition = a.applicationPhase.hiringStage?.position || 0
  const bStagePosition = b.applicationPhase.hiringStage?.position || 0
  if (aStagePosition !== bStagePosition) return bStagePosition - aStagePosition

  // Applications on latter phases first
  const aPhasePosition = a.applicationPhase.position || 0
  const bPhasePosition = b.applicationPhase.position || 0
  if (aPhasePosition !== bPhasePosition) return bPhasePosition - aPhasePosition

  // Applications created more recently first
  return diffBetweenDateTimes(b.createdAt, a.createdAt)
}

const getApplicationStatusColor = (application: RowApplication) => {
  if (application.qualified && isJobActive(application.jobPosting.status)) return 'grey800'

  return 'grey700'
}

const getApplicationPhaseColor = (application: RowApplication) => {
  const hiringStage = application.applicationPhase.hiringStage
  if (!hiringStage) {
    return 'grey400'
  }

  return getProperties(hiringStage.name).color
}

interface Props {
  candidate: DataTableRow
  maximum: number
}
export const JobsList = ({ candidate, maximum }: Props) => {
  const allJobs = uniq(candidate.atsJobPostingIds || [])
  const allJobsCount = allJobs.length
  const applicationsToShow: RowApplication[] = []

  candidate.applications
    .slice(0)
    .sort(prioritizeApplications)
    .forEach((application) => {
      const jobIndex = allJobs.indexOf(application.jobPosting.id)
      if (jobIndex !== -1 && applicationsToShow.length < maximum) {
        allJobs.splice(jobIndex, 1)
        applicationsToShow.push(application)
      }
    })

  const moreJobs = allJobsCount - applicationsToShow.length

  return (
    <>
      {applicationsToShow.map((application) => (
        <Box
          key={`job_${application.id}`}
          flexDirection="row"
          alignItems="center"
          gap="s4"
          flexWrap="noWrap"
          overflowX="hidden"
        >
          <Box flexNoShrink>
            <InfoDot size="small" color={getApplicationPhaseColor(application)} />
          </Box>
          <Text.Normal ellipsis color={getApplicationStatusColor(application)}>
            {application.jobPosting.title}
          </Text.Normal>
        </Box>
      ))}
      {moreJobs > 0 && (
        <Text.Normal>
          {i18n.t('ats.candidates.more_jobs', {
            count: moreJobs,
          })}
        </Text.Normal>
      )}
    </>
  )
}
